<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, shallowRef } from 'vue'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { setPageTitle } from '@/@core/utils/pageMetaUtils'
import LazyDashboardCard from '@/components/Dashboard/LazyDashboardCard.vue'
import CommerceRevenueSection from '@/views/dashboards/ecommerce/CommerceRevenueSection.vue'
import EcommerceDashboardHeader from '@/views/dashboards/ecommerce/EcommerceDashboardHeader.vue'
import {
  COMMERCE_DISPLAYABLE_METRIC_COUNT,
  CommerceMetricId,
} from '@/views/dashboards/ecommerce/dashboardMetrics'
import { useCommerceDashboardMetrics } from '@/views/dashboards/ecommerce/useCommerceDashboardMetrics'

setPageTitle(PageMeta.Dashboard.ecommerce.title)
definePage({ meta: PageMeta.Dashboard.ecommerce.meta })

const CommerceMetricsGrid = defineAsyncComponent(
  () => import('@/views/dashboards/ecommerce/CommerceMetricsGrid.vue')
)

const {
  dashboardModel,
  hasErrors,
  isLoading,
  lastUpdatedAt,
  pendingMetricIds,
  fetchDashboardMetrics,
} = useCommerceDashboardMetrics()

const revenueMetricIds = [CommerceMetricId.InvoiceRevenue, CommerceMetricId.InvoiceMonthlyRevenue]

const deferredMetricIds = Object.values(CommerceMetricId).filter(
  metricId => !revenueMetricIds.includes(metricId)
)

const hasRequestedDeferredMetrics = shallowRef(false)

const recurringRevenueValue = computed(
  () =>
    dashboardModel.value.metricCards.find(
      metric => metric.id === CommerceMetricId.QuoteRecurringRevenue
    )?.value
)

const upcomingEventsValue = computed(
  () =>
    dashboardModel.value.metricCards.find(metric => metric.id === CommerceMetricId.CalendarUpcoming)
      ?.value ?? 0
)

async function handleRefresh() {
  await fetchDashboardMetrics(
    hasRequestedDeferredMetrics.value ? Object.values(CommerceMetricId) : revenueMetricIds
  )
}

async function handleDeferredMetricsVisible() {
  if (hasRequestedDeferredMetrics.value) {
    return
  }

  hasRequestedDeferredMetrics.value = true
  await fetchDashboardMetrics(deferredMetricIds)
}

onMounted(handleRefresh)
</script>

<template>
  <main class="ecommerce-dashboard">
    <EcommerceDashboardHeader
      :is-loading="isLoading"
      :has-errors="hasErrors"
      :last-updated-at="lastUpdatedAt"
      :metric-count="COMMERCE_DISPLAYABLE_METRIC_COUNT"
      :revenue-value="dashboardModel.revenue.value"
      :recurring-revenue-value="recurringRevenueValue"
      :upcoming-events-value="upcomingEventsValue"
      @refresh="handleRefresh"
    />

    <CommerceRevenueSection
      :revenue="dashboardModel.revenue"
      :monthly-revenue="dashboardModel.monthlyRevenue"
      :pending-metric-ids="pendingMetricIds"
    />

    <LazyDashboardCard
      root-margin="920px"
      :min-delay="80"
      @visible="handleDeferredMetricsVisible"
    >
      <CommerceMetricsGrid
        :invoice-status="dashboardModel.invoiceStatus"
        :quote-status="dashboardModel.quoteStatus"
        :metric-cards="dashboardModel.metricCards"
        :pending-metric-ids="pendingMetricIds"
      />
    </LazyDashboardCard>
  </main>
</template>

<style lang="scss" scoped>
@use '@core/scss/template/libs/apex-chart.scss';

.ecommerce-dashboard {
  display: grid;
  min-inline-size: 0;
  gap: clamp(1.75rem, 3vw, 2.5rem);
}

@media (max-width: 599px) {
  .ecommerce-dashboard {
    gap: 1.5rem;
  }
}
</style>
